/**
 * SPDX-FileCopyrightText: 2024 Nextcloud GmbH and Nextcloud contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */
import { loadState } from '@nextcloud/initial-state'
import logger from './logger.js'
import { fixAccountId } from './service/AccountService.js'
import { fetchAvailableLanguages } from './service/translationService.js'
import useMainStore from './store/mainStore.js'
import useOutboxStore from './store/outboxStore.js'

export default function initAfterAppCreation() {
	logger.debug('Init after app creation')
	const mainStore = useMainStore()

	const preferences = loadState('mail', 'preferences', [])

	mainStore.savePreferenceMutation({
		key: 'debug',
		value: loadState('mail', 'debug', false),
	})
	mainStore.savePreferenceMutation({
		key: 'ncVersion',
		value: loadState('mail', 'ncVersion'),
	})
	mainStore.savePreferenceMutation({
		key: 'mailVersion',
		value: loadState('mail', 'mailVersion'),
	})

	mainStore.savePreferenceMutation({
		key: 'sort-order',
		value: loadState('mail', 'sort-order', 'newest'),
	})

	mainStore.savePreferenceMutation({
		key: 'attachment-size-limit',
		value: Number.parseInt(preferences['attachment-size-limit'], 10),
	})
	mainStore.savePreferenceMutation({
		key: 'version',
		value: preferences['config-installed-version'],
	})
	mainStore.savePreferenceMutation({
		key: 'external-avatars',
		value: preferences['external-avatars'],
	})
	mainStore.savePreferenceMutation({
		key: 'collect-data',
		value: preferences['collect-data'],
	})
	mainStore.savePreferenceMutation({
		key: 'search-priority-body',
		value: preferences['search-priority-body'],
	})
	mainStore.savePreferenceMutation({
		key: 'sort-favorites',
		value: preferences['sort-favorites'],
	})
	let startMailboxId = preferences['start-mailbox-id']
	if (startMailboxId && !['unified', 'priority'].includes(startMailboxId)) {
		startMailboxId = parseInt(startMailboxId, 10)
	}
	mainStore.savePreferenceMutation({
		key: 'start-mailbox-id',
		value: startMailboxId,
	})
	mainStore.savePreferenceMutation({
		key: 'index-context-chat',
		value: preferences['index-context-chat'],
	})
	mainStore.savePreferenceMutation({
		key: 'allow-new-accounts',
		value: loadState('mail', 'allow-new-accounts', true),
	})
	mainStore.savePreferenceMutation({
		key: 'password-is-unavailable',
		value: loadState('mail', 'password-is-unavailable', false),
	})
	mainStore.savePreferenceMutation({
		key: 'layout-mode',
		value: preferences['layout-mode'],
	})
	mainStore.savePreferenceMutation({
		key: 'layout-message-view',
		value: preferences['layout-message-view'],
	})
	mainStore.savePreferenceMutation({
		key: 'follow-up-reminders',
		value: preferences['follow-up-reminders'],
	})
	mainStore.savePreferenceMutation({
		key: 'internal-addresses',
		value: loadState('mail', 'internal-addresses', false),
	})
	mainStore.savePreferenceMutation({
		key: 'smime-sign-aliases',
		value: loadState('mail', 'smime-sign-aliases', []),
	})
	mainStore.savePreferenceMutation({
		key: 'compact-mode',
		value: preferences['compact-mode'],
	})
	mainStore.savePreferenceMutation({
		key: 'auto-mark-as-read',
		value: preferences['auto-mark-as-read'],
	})

	mainStore.setQuickActions(loadState('mail', 'quick-actions', []))

	const accountSettings = loadState('mail', 'account-settings')
	const accounts = loadState('mail', 'accounts', [])
	const internalAddressesList = loadState('mail', 'internal-addresses-list', [])
	const tags = loadState('mail', 'tags', [])
	const outboxMessages = loadState('mail', 'outbox-messages')
	const disableScheduledSend = loadState('mail', 'disable-scheduled-send')
	const disableSnooze = loadState('mail', 'disable-snooze')
	const googleOauthUrl = loadState('mail', 'google-oauth-url', null)
	const microsoftOauthUrl = loadState('mail', 'microsoft-oauth-url', null)
	const followUpFeatureAvailable = loadState('mail', 'llm_followup_available', false)
	const contextChatFeatureAvailable = loadState('mail', 'context_chat_available', false)

	accounts.map(fixAccountId).forEach((account) => {
		const settings = accountSettings.find((settings) => settings.accountId === account.id)
		if (settings) {
			delete settings.accountId
			Object.entries(settings).forEach(([key, value]) => {
				mainStore.setAccountSettingMutation({
					accountId: account.id,
					key,
					value,
				})
			})
		}
		mainStore.addAccountMutation({ ...account, ...settings })
	})

	tags.forEach((tag) => mainStore.addTagMutation({ tag }))
	internalAddressesList.forEach((internalAddress) => mainStore.addInternalAddressMutation(internalAddress))

	mainStore.setScheduledSendingDisabledMutation(disableScheduledSend)
	mainStore.setSnoozeDisabledMutation(disableSnooze)
	mainStore.setGoogleOauthUrlMutation(googleOauthUrl)
	mainStore.setMicrosoftOauthUrlMutation(microsoftOauthUrl)
	mainStore.setFollowUpFeatureAvailableMutation(followUpFeatureAvailable)
	mainStore.setContextChatFeatureAvailableMutation(contextChatFeatureAvailable)

	const smimeCertificates = loadState('mail', 'smime-certificates', [])
	mainStore.setSmimeCertificatesMutation(smimeCertificates)

	const outboxStore = useOutboxStore()
	outboxMessages.forEach((message) => outboxStore.addMessageMutation({ message }))

	const llmTranslationEnabled = loadState('mail', 'llm_translation_enabled', false)
	mainStore.isTranslationEnabled = llmTranslationEnabled
	if (llmTranslationEnabled) {
		fetchAvailableLanguages().then(() => {})
	}
}
